import React from 'react';
import { t } from '../../i18n';

const PatientDetailsModal = ({
  showModal,
  selectedPatient,
  onClose,
  onEdit,
  lang
}) => {
  if (!showModal || !selectedPatient) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {t("patientDetails", lang) || "Patient Details"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Patient Avatar and Basic Info */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg mr-4">
                  <span className="text-white font-bold text-2xl">
                    {selectedPatient.fullName?.charAt(0)?.toUpperCase() ||
                      "P"}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedPatient.fullName}
                  </h3>
                  <p className="text-gray-600">{selectedPatient.email}</p>
                  <p className="text-sm text-gray-500">
                    Member ID: {selectedPatient.memberId}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date of Birth:</span>
                  <span className="font-medium">
                    {selectedPatient.dob && selectedPatient.dob.trim() ? selectedPatient.dob : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gender:</span>
                  <span className="font-medium">
                    {selectedPatient.gender && selectedPatient.gender.trim() ? selectedPatient.gender : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Blood Type:</span>
                  <span className="font-medium">
                    {selectedPatient.bloodType && selectedPatient.bloodType.trim() ? selectedPatient.bloodType : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">
                    {selectedPatient.primaryPhone && selectedPatient.primaryPhone.trim() ? selectedPatient.primaryPhone : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                      selectedPatient.status === "active"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full mr-2 ${
                        selectedPatient.status === "active"
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    ></div>
                    {selectedPatient.status === "active"
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {t("contactInformation", lang) || "Contact Information"}
              </h4>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-600 block">Address:</span>
                  <span className="font-medium">
                    {(() => {
                      const addr1 = selectedPatient.address1 && selectedPatient.address1.trim();
                      const addr2 = selectedPatient.address2 && selectedPatient.address2.trim();
                      if (addr1 && addr2) return `${addr1}, ${addr2}`;
                      if (addr1) return addr1;
                      return "Not specified";
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">City:</span>
                  <span className="font-medium">
                    {selectedPatient.city && selectedPatient.city.trim() ? selectedPatient.city : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">State:</span>
                  <span className="font-medium">
                    {selectedPatient.state && selectedPatient.state.trim() ? selectedPatient.state : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Postal Code:</span>
                  <span className="font-medium">
                    {selectedPatient.postalCode && selectedPatient.postalCode.trim() ? selectedPatient.postalCode : "Not specified"}
                  </span>
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {t("medicalInformation", lang) || "Medical Information"}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Height:</span>
                  <span className="font-medium">
                    {selectedPatient.height && selectedPatient.height.trim()
                      ? `${selectedPatient.height} inches`
                      : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Weight:</span>
                  <span className="font-medium">
                    {selectedPatient.weight && selectedPatient.weight.trim()
                      ? `${selectedPatient.weight} lbs`
                      : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Insurance:</span>
                  <span className="font-medium">
                    {selectedPatient.insurance && selectedPatient.insurance.trim() ? selectedPatient.insurance : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Organ Donor:</span>
                  <span className="font-medium">
                    {selectedPatient.organDonor ? selectedPatient.organDonor : "Not specified"}
                  </span>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {t("emergencyContact", lang) || "Emergency Contact"}
              </h4>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-600 block">Name:</span>
                  <span className="font-medium">
                    {selectedPatient.emergencyName && selectedPatient.emergencyName.trim() ? selectedPatient.emergencyName : "Not specified"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 block">Phone:</span>
                  <span className="font-medium">
                    {selectedPatient.emergencyPhone && selectedPatient.emergencyPhone.trim() ? selectedPatient.emergencyPhone : "Not specified"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 block">Relation:</span>
                  <span className="font-medium">
                    {selectedPatient.emergencyRelation && selectedPatient.emergencyRelation.trim() ? selectedPatient.emergencyRelation : "Not specified"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              {t("close", lang) || "Close"}
            </button>
            <button
              onClick={onEdit}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200"
            >
              {t("editPatient", lang) || "Edit Patient"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsModal; 